import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Form, Button, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { RiskAssessment, Sector, Source, RiskType, PotentialDamage, Ocupacion } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite } from "../../../../contexts/SiteContext";
import { useLanguage } from "../../../../contexts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import "./RiskAssessmentForm.scss";

const riskAssessmentController = new RiskAssessment();
const sectorController         = new Sector();
const sourceController         = new Source();
const riskTypeController       = new RiskType();
const potentialDamageController = new PotentialDamage();
const ocupacionController      = new Ocupacion();

// ── Matrices ────────────────────────────────────────────────
const MATRIX_5x5 = {
  5: { 1:"MODERADO", 2:"ALTO",     3:"ALTO",     4:"CRITICO",  5:"CRITICO"  },
  4: { 1:"BAJO",     2:"MODERADO", 3:"ALTO",     4:"ALTO",     5:"CRITICO"  },
  3: { 1:"BAJO",     2:"MODERADO", 3:"MODERADO", 4:"ALTO",     5:"ALTO"     },
  2: { 1:"BAJO",     2:"BAJO",     3:"MODERADO", 4:"MODERADO", 5:"ALTO"     },
  1: { 1:"BAJO",     2:"BAJO",     3:"BAJO",     4:"BAJO",     5:"MODERADO" },
};

const INSST_MATRIX = {
  A: { ED:"CRITICO",  D:"ALTO",     LD:"MODERADO" },
  M: { ED:"ALTO",     D:"MODERADO", LD:"BAJO"     },
  B: { ED:"MODERADO", D:"BAJO",     LD:"BAJO"     },
};

const LEVEL_TO_RESULT = {
  CRITICO:  "CRITICO",
  ALTO:     "ALTO",
  MODERADO: "MODERADO",
  BAJO:     "BAJO",
};

const LEVEL_TO_RESIDUAL = {
  CRITICO:  "CRITICO",
  ALTO:     "ALTO",
  MODERADO: "MODERADO",
  BAJO:     "BAJO",
};

const LEVEL_CONFIG = {
  CRITICO:  { color: "#B23030", bg: "#F8D5D5" },
  ALTO:     { color: "#E07020", bg: "#FBE1CC" },
  MODERADO: { color: "#D4960F", bg: "#FEF3D6" },
  BAJO:     { color: "#2E9461", bg: "#D7EFE0" },
};

// ── Opciones ─────────────────────────────────────────────────
// ── Shortcuts para cada catálogo ────────────────────────────
function OcupacionDropdown({ accessToken, value, onChange, canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  return <CatalogDBDropdown accessToken={accessToken} fetchFn={(tk, a) => ocupacionController.getOcupaciones(tk, a)} createFn={(tk, d) => ocupacionController.createOcupacion(tk, d)} resultKey="occupation" label={t("raf_new_occupation")} placeholder={t("raf_select_occupation")} value={value} onChange={onChange} canAdd={canAdd} />;
}
function SourceDropdown({ accessToken, value, onChange, canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  return <CatalogDBDropdown accessToken={accessToken} fetchFn={(tk, a) => sourceController.getSources(tk, a)} createFn={(tk, d) => sourceController.createSource(tk, d)} resultKey="source" label={t("raf_new_source")} placeholder={t("raf_select_source")} value={value} onChange={onChange} canAdd={canAdd} />;
}
function RiskTypeDropdown({ accessToken, value, onChange, canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  return <CatalogDBDropdown accessToken={accessToken} fetchFn={(tk, a) => riskTypeController.getRiskTypes(tk, a)} createFn={(tk, d) => riskTypeController.createRiskType(tk, d)} resultKey="riskType" label={t("raf_new_risk_type")} placeholder={t("raf_select_risk_type")} value={value} onChange={onChange} canAdd={canAdd} />;
}
function PotentialDamageDropdown({ accessToken, value, onChange, canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  return <CatalogDBDropdown accessToken={accessToken} fetchFn={(tk, a) => potentialDamageController.getPotentialDamages(tk, a)} createFn={(tk, d) => potentialDamageController.createPotentialDamage(tk, d)} resultKey="potentialDamage" label={t("raf_new_damage_type")} placeholder={t("raf_select_damage")} value={value} onChange={onChange} canAdd={canAdd} />;
}


// ── CatalogDBDropdown — dropdown genérico respaldado en BD ──
function CatalogDBDropdown({ accessToken, fetchFn, createFn, resultKey, label, placeholder, value, onChange, canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [options, setOptions] = React.useState([]);
  const [open,    setOpen]    = React.useState(false);
  const [code,    setCode]    = React.useState("");
  const [name,    setName]    = React.useState("");
  const [saving,  setSaving]  = React.useState(false);
  const [error,   setError]   = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetchFn(accessToken, true);
        setOptions((res || []).map(s => ({ key: s._id, value: s.name, text: s.name })));
      } catch {}
    })();
  }, [accessToken]);

  const allOptions = [
    ...options,
    { key: "__ADD__", value: "__ADD__", text: `Agregar ${label.startsWith("nueva") || label.startsWith("nuevo") ? label : `nueva ${label}`}`, icon: "plus circle" },
  ];

  const handleChange = (_, { value: val }) => {
    if (val !== "__ADD__") { onChange(val); return; }
    setCode(""); setName(""); setError(""); setOpen(true);
  };

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) { setError(t("raf_code_name_required")); return; }
    setSaving(true);
    try {
      const res = await createFn(accessToken, { code: code.trim(), name: name.trim() });
      const created = res[resultKey];
      setOptions(prev => [...prev, { key: created._id, value: created.name, text: created.name }]);
      onChange(created.name);
      setOpen(false);
    } catch (err) {
      setError(err?.msg || t("raf_save_error_catalog"));
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px",
    border: "1px solid rgba(34,36,38,.15)", borderRadius: ".28571429rem",
    fontSize: "14px", outline: "none",
    background: "#fff", color: "rgba(0,0,0,.87)",
    boxSizing: "border-box", fontFamily: "inherit",
    transition: "border-color .1s ease,box-shadow .1s ease",
  };

  return (
    <>
      <select className="ra-inline-select" value={value || ""} onChange={e => {
        const val = e.target.value;
        if (val !== "__ADD__") { onChange(val); return; }
        setCode(""); setName(""); setError(""); setOpen(true);
      }}>
        <option value="">{placeholder || t("raf_select")}</option>
        {options.map(o => (
          <option key={o.key} value={o.value}>{o.text}</option>
        ))}
        {canAdd && <option value="__ADD__">{t("raf_add_new")}</option>}
      </select>
      {open && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(400px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
            <p style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "20px", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif" }}>{label}</p>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>{t("raf_code")} <span style={{ color: "#e53935" }}>*</span></div>
              <input autoFocus placeholder="Ej: MC" value={code} onChange={e => { setCode(e.target.value); setError(""); }} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>{t("raf_name_label")} <span style={{ color: "#e53935" }}>*</span></div>
              <input placeholder="Ej: Mecánico" value={name} onChange={e => { setName(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && handleSave()} style={inputStyle} />
            </div>
            {error && <p style={{ color: "#e53935", fontSize: "12px", marginTop: "6px", marginBottom: 0 }}>{error}</p>}
            <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "26px" }}>
              <button
                onClick={handleSave}
                disabled={saving}
                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#3a6a8e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#4A7FA5"; }}
                style={{ background: "#4A7FA5", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, transition: "background 0.15s" }}
              >
                {saving ? t("raf_saving") : t("save")}
              </button>
              <button
                onClick={() => setOpen(false)}
                onMouseEnter={e => { e.currentTarget.style.background = "#8099B0"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#9BAFC4"; }}
                style={{ background: "#9BAFC4", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", transition: "background 0.15s" }}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ── Lógica catálogos custom (localStorage) ──────────────────
const CATALOG_KEY = "sustemia_custom_catalogs_v1";

function loadCustom(key) {
  try { return JSON.parse(localStorage.getItem(CATALOG_KEY) || "{}")[key] || []; }
  catch { return []; }
}
function saveCustom(key, values) {
  try {
    const stored = JSON.parse(localStorage.getItem(CATALOG_KEY) || "{}");
    stored[key] = values;
    localStorage.setItem(CATALOG_KEY, JSON.stringify(stored));
  } catch {}
}

function CatalogDropdown({ catalogKey, baseOptions, value, onChange, placeholder, label = "valor", canAdd }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [customs,   setCustoms]   = React.useState(() => loadCustom(catalogKey));
  const [open,      setOpen]      = React.useState(false);
  const [newVal,    setNewVal]    = React.useState("");
  const [error,     setError]     = React.useState("");

  const allOptions = [
    ...baseOptions,
    ...customs.map(v => ({ key: `__custom__${v}`, value: v, text: v })),
    { key: "__ADD__", value: "__ADD__", text: `Agregar nueva ${label}`, icon: "plus circle" },
  ];

  const handleChange = (_, { value: val }) => {
    if (val !== "__ADD__") { onChange(val); return; }
    setNewVal("");
    setError("");
    setOpen(true);
  };

  const handleConfirm = () => {
    const trimmed = newVal.trim();
    if (!trimmed) { setError(t("raf_enter_name")); return; }
    const existing = [
      ...baseOptions.map(o => o.text.toLowerCase()),
      ...customs.map(v => v.toLowerCase()),
    ];
    if (existing.includes(trimmed.toLowerCase())) {
      setError(t("raf_value_exists"));
      return;
    }
    const updated = [...customs, trimmed];
    setCustoms(updated);
    saveCustom(catalogKey, updated);
    onChange(trimmed);
    setOpen(false);
  };

  return (
    <>
      <select className="ra-inline-select" value={value || ""} onChange={e => {
        const val = e.target.value;
        if (val !== "__ADD__") { onChange(val); return; }
        setNewVal(""); setError(""); setOpen(true);
      }}>
        <option value="">{placeholder || "Seleccionar..."}</option>
        {allOptions.filter(o => o.key !== "__ADD__").map(o => (
          <option key={o.key} value={o.value}>{o.text}</option>
        ))}
        {canAdd && <option value="__ADD__">{t("raf_add_new")}</option>}
      </select>
      {open && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(400px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
            <p style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "20px", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif" }}>
              {label}
            </p>
            <div style={{ marginBottom: "6px", fontWeight: 600, fontSize: "13px", color: "#374151" }}>{t("raf_name_label")} <span style={{ color: "#e53935" }}>*</span></div>
            <input
              autoFocus
              placeholder={`Ej: ${label === "fuente" ? "Agente térmico" : label === "tipo de riesgo" ? "Térmico / Quemadura" : "Daño auditivo"}`}
              value={newVal}
              onChange={e => { setNewVal(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleConfirm()}
              style={{ width: "100%", padding: "10px 14px", border: "1px solid rgba(34,36,38,.15)", borderRadius: ".28571429rem", fontSize: "14px", outline: "none", background: "#fff", color: "rgba(0,0,0,.87)", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color .1s ease" }}
            />
            {error && <p style={{ color: "#e53935", fontSize: "12px", marginTop: "6px", marginBottom: 0 }}>{error}</p>}
            <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "26px" }}>
              <button
                onClick={handleConfirm}
                onMouseEnter={e => { e.currentTarget.style.background = "#3a6a8e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#4A7FA5"; }}
                style={{ background: "#4A7FA5", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", transition: "background 0.15s" }}
              >{t("save")}</button>
              <button
                onClick={() => setOpen(false)}
                onMouseEnter={e => { e.currentTarget.style.background = "#8099B0"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#9BAFC4"; }}
                style={{ background: "#9BAFC4", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", transition: "background 0.15s" }}
              >{t("cancel")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const HAZARD_SOURCE_OPTIONS = [
  { key: "machinery_equipment",            value: "machinery_equipment",            text: "Máquina / Equipo" },
  { key: "hand_electric_tool",             value: "hand_electric_tool",             text: "Herramienta manual / eléctrica" },
  { key: "electrical_installation",        value: "electrical_installation",        text: "Instalación eléctrica" },
  { key: "hazardous_chemical",             value: "hazardous_chemical",             text: "Sustancia química peligrosa" },
  { key: "physical_agent",                value: "physical_agent",                text: "Agente físico (ruido, vibración, temperatura, iluminación)" },
  { key: "biological_agent",              value: "biological_agent",              text: "Agente biológico" },
  { key: "physical_workload",             value: "physical_workload",             text: "Carga física / esfuerzo" },
  { key: "work_organization_psychosocial", value: "work_organization_psychosocial", text: "Organización del trabajo / factores psicosociales" },
  { key: "order_cleanliness",             value: "order_cleanliness",             text: "Orden y limpieza" },
  { key: "height_work",                   value: "height_work",                   text: "Altura / Trabajos en altura" },
  { key: "vehicles_forklifts",            value: "vehicles_forklifts",            text: "Vehículos / Montacargas / Autoelevadores" },
  { key: "confined_space",               value: "confined_space",               text: "Espacio confinado" },
  { key: "construction_work",            value: "construction_work",            text: "Obra / Construcción" },
  { key: "other",                         value: "other",                         text: "Otro" },
];

const RISK_TYPE_OPTIONS = [
  { key: "mechanical",                     value: "mechanical",                     text: "Mecánico (corte, atrapamiento, golpe, caída)" },
  { key: "electrical",                     value: "electrical",                     text: "Eléctrico" },
  { key: "ergonomic",                      value: "ergonomic",                      text: "Ergonómico" },
  { key: "chemical",                       value: "chemical",                       text: "Químico" },
  { key: "physical_noise",                value: "physical_noise",                text: "Físico — Ruido" },
  { key: "physical_vibration",            value: "physical_vibration",            text: "Físico — Vibración" },
  { key: "physical_extreme_temperature",  value: "physical_extreme_temperature",  text: "Físico — Temperatura extrema" },
  { key: "physical_insufficient_lighting", value: "physical_insufficient_lighting", text: "Físico — Iluminación insuficiente" },
  { key: "physical_radiation",            value: "physical_radiation",            text: "Físico — Radiación" },
  { key: "biological",                    value: "biological",                    text: "Biológico" },
  { key: "psychosocial_work_stress",      value: "psychosocial_work_stress",      text: "Psicosocial / Estrés laboral" },
  { key: "fire_explosion",               value: "fire_explosion",               text: "Incendio / Explosión" },
  { key: "falling_objects",              value: "falling_objects",              text: "Caída de objetos" },
  { key: "same_level_fall",              value: "same_level_fall",              text: "Caída al mismo nivel" },
  { key: "different_level_fall",         value: "different_level_fall",         text: "Caída a distinto nivel" },
  { key: "locative",                     value: "locative",                     text: "Locativo" },
  { key: "other",                         value: "other",                         text: "Otro" },
];

const POTENTIAL_DAMAGE_OPTIONS = [
  { key: "death",                              value: "death",                              text: "Muerte" },
  { key: "severe_injury_permanent_disability", value: "severe_injury_permanent_disability", text: "Lesión grave / incapacidad permanente" },
  { key: "moderate_injury_lost_days",          value: "moderate_injury_lost_days",          text: "Lesión moderada / días perdidos" },
  { key: "minor_injury_first_aid",             value: "minor_injury_first_aid",             text: "Lesión leve / primeros auxilios" },
  { key: "material_damage_no_injury",          value: "material_damage_no_injury",          text: "Daño material / sin lesión" },
];

// Opciones 5×5 (labels del HTML)
const SEVERITY_5x5 = [
  { key: "5", value: 5, tKey: "sev5_5" },
  { key: "4", value: 4, tKey: "sev5_4" },
  { key: "3", value: 3, tKey: "sev5_3" },
  { key: "2", value: 2, tKey: "sev5_2" },
  { key: "1", value: 1, tKey: "sev5_1" },
];

const FOE_OPTIONS = [
  { key: "5", value: 5, tKey: "foe_5" },
  { key: "4", value: 4, tKey: "foe_4" },
  { key: "3", value: 3, tKey: "foe_3" },
  { key: "2", value: 2, tKey: "foe_2" },
  { key: "1", value: 1, tKey: "foe_1" },
];

const SEVERITY_5x5_SHORT = [
  { key: "5", value: 5, tKey: "sev_5" },
  { key: "4", value: 4, tKey: "sev_4" },
  { key: "3", value: 3, tKey: "sev_3" },
  { key: "2", value: 2, tKey: "sev_2" },
  { key: "1", value: 1, tKey: "sev_1" },
];

const FOE_SHORT = [
  { key: "5", value: 5, tKey: "foe_short_5" },
  { key: "4", value: 4, tKey: "foe_short_4" },
  { key: "3", value: 3, tKey: "foe_short_3" },
  { key: "2", value: 2, tKey: "foe_short_2" },
  { key: "1", value: 1, tKey: "foe_short_1" },
];

// NTP 330
const ND_OPTIONS = [
  { key: "10", value: 10, tKey: "nd_10" },
  { key: "6",  value: 6,  tKey: "nd_6"  },
  { key: "2",  value: 2,  tKey: "nd_2"  },
  { key: "0",  value: 0,  tKey: "nd_0"  },
];
const NE_OPTIONS = [
  { key: "4", value: 4, tKey: "ne_4" },
  { key: "3", value: 3, tKey: "ne_3" },
  { key: "2", value: 2, tKey: "ne_2" },
  { key: "1", value: 1, tKey: "ne_1" },
];
const NC_OPTIONS = [
  { key: "100", value: 100, tKey: "nc_100" },
  { key: "60",  value: 60,  tKey: "nc_60"  },
  { key: "25",  value: 25,  tKey: "nc_25"  },
  { key: "10",  value: 10,  tKey: "nc_10"  },
];

// INSST
const PROB_OPTIONS = [
  { key: "A", value: "A", tKey: "prob_A" },
  { key: "M", value: "M", tKey: "prob_M" },
  { key: "B", value: "B", tKey: "prob_B" },
];
const CONS_OPTIONS = [
  { key: "ED", value: "ED", tKey: "cons_ED" },
  { key: "D",  value: "D",  tKey: "cons_D"  },
  { key: "LD", value: "LD", tKey: "cons_LD" },
];

const CONTROL_HIERARCHY_OPTIONS = [
  { key: "1_elimination",          value: "1_elimination"          },
  { key: "2_substitution",         value: "2_substitution"         },
  { key: "3_engineering_control",  value: "3_engineering_control"  },
  { key: "4_administrative_control", value: "4_administrative_control" },
  { key: "5_ppe",                  value: "5_ppe"                  },
];

const CONTROL_STATUS_OPTIONS = [
  { key: "IMPL", value: "IMPL", tKey: "ctrl_impl" },
  { key: "PROG", value: "PROG", tKey: "ctrl_prog" },
  { key: "PLAN", value: "PLAN", tKey: "ctrl_plan" },
  { key: "NONE", value: "NONE", tKey: "ctrl_none" },
];

// ── Helpers de cálculo ───────────────────────────────────────
function calc5x5Level(sev, foe) {
  if (!sev || !foe) return null;
  return MATRIX_5x5[Number(sev)]?.[Number(foe)] || null;
}

function calcNTPLevel(nd, ne, nc) {
  if (nd === "" || nd === undefined || !ne || !nc) return null;
  const nr = Number(nd) * Number(ne) * Number(nc);
  if (nr >= 600) return "CRITICO";
  if (nr >= 150) return "ALTO";
  if (nr >= 40)  return "MODERADO";
  return "BAJO";
}

function calcNTPValues(nd, ne, nc) {
  if (nd === "" || nd === undefined || !ne || !nc) return null;
  const np = Number(nd) * Number(ne);
  const nr = np * Number(nc);
  return { np, nr };
}

function calcINSSTLevel(prob, cons) {
  if (!prob || !cons) return null;
  return INSST_MATRIX[prob]?.[cons] || null;
}

// ── Subcomponentes ────────────────────────────────────────────
function SectionCard({ title, children, accent, collapsible }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div className={`ra-section-card${accent ? " accent-" + accent : ""}`}>
      {collapsible ? (
        <button className="ra-section-title ra-section-title--btn" type="button" onClick={() => setOpen(o => !o)}>
          <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"
            style={{ flexShrink: 0, transition: "transform 0.2s", transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>
            <path d="M6 8L2 4h8z"/>
          </svg>
          {title}
        </button>
      ) : (
        <h3 className="ra-section-title">{title}</h3>
      )}
      {(!collapsible || open) && <div className="ra-section-body">{children}</div>}
    </div>
  );
}

function CalcResult({ formula, value, level }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const cfg = level ? LEVEL_CONFIG[level] : null;
  return (
    <div className="ra-calc-result">
      <div className="ra-calc-formula">{formula}</div>
      <div className="ra-calc-value">
        <span className="ra-calc-num">{value ?? "—"}</span>
        {cfg && (
          <span className="ra-calc-pill" style={{ background: cfg.bg, color: cfg.color }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, display: "inline-block", flexShrink: 0 }} />
            {t("risk_level_" + level.toLowerCase())}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Catálogo partes del cuerpo ───────────────────────────────────
const BODY_PARTS = {
  'Cabeza y cara': ['Cabeza (general)', 'Cráneo / Cuero cabelludo', 'Cara', 'Ojos', 'Oídos', 'Nariz', 'Boca / Dientes / Labios', 'Mandíbula'],
  'Cuello y tronco': ['Cuello', 'Garganta', 'Hombros', 'Espalda (general)', 'Espalda — zona cervical', 'Espalda — zona dorsal', 'Espalda — zona lumbar', 'Pecho / Tórax', 'Abdomen', 'Pelvis / Cadera', 'Órganos internos (genérico)'],
  'Miembros superiores': ['Brazos (general)', 'Brazo (superior)', 'Codo', 'Antebrazo', 'Muñeca', 'Mano', 'Manos (ambas)', 'Dedos de la mano', 'Palma de la mano'],
  'Miembros inferiores': ['Piernas (general)', 'Muslo', 'Rodilla', 'Pierna (inferior)', 'Tobillo', 'Pie', 'Pies (ambos)', 'Dedos del pie', 'Planta del pie'],
  'Sistemas y aparatos': ['Sistema respiratorio / Vías respiratorias', 'Pulmones', 'Sistema cardiovascular', 'Sistema nervioso', 'Sistema digestivo', 'Sistema auditivo', 'Sistema visual', 'Sistema musculoesquelético', 'Piel (general)', 'Aparato reproductor'],
  'Lesiones generales': ['Cuerpo entero', 'Múltiples partes del cuerpo', 'Lesión generalizada / sistémica', 'No aplica / Sin lesión personal'],
};

const BP_CUSTOM_KEY = 'sustemia_custom_body_parts_v1';
function loadCustomBodyParts() {
  try { return JSON.parse(localStorage.getItem(BP_CUSTOM_KEY)) || []; } catch { return []; }
}
function saveCustomBodyParts(arr) {
  try { localStorage.setItem(BP_CUSTOM_KEY, JSON.stringify(arr)); } catch {}
}

function BodyPartsSelect({ value = [], onChange }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [search, setSearch]   = React.useState('');
  const [open, setOpen]       = React.useState(false);
  const [customs, setCustoms] = React.useState(loadCustomBodyParts);
  const [adding, setAdding]   = React.useState(false);
  const [newPart, setNewPart] = React.useState('');
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (part) => {
    if (value.includes(part)) onChange(value.filter(v => v !== part));
    else onChange([...value, part]);
  };

  const addCustom = () => {
    const t = newPart.trim();
    if (!t) return;
    const all = Object.values(BODY_PARTS).flat().concat(customs);
    if (all.some(p => p.toLowerCase() === t.toLowerCase())) { setNewPart(''); setAdding(false); return; }
    const updated = [...customs, t];
    setCustoms(updated);
    saveCustomBodyParts(updated);
    onChange([...value, t]);
    setNewPart(''); setAdding(false);
  };

  const q = search.toLowerCase();
  const groups = Object.entries(BODY_PARTS).map(([g, parts]) => ({
    group: g,
    items: parts.filter(p => !q || p.toLowerCase().includes(q)),
  })).filter(g => g.items.length > 0);
  const customFiltered = customs.filter(p => !q || p.toLowerCase().includes(q));

  return (
    <div className="bp-select" ref={wrapRef}>
      <div className="bp-chips" onClick={() => setOpen(true)}>
        {value.length === 0 && <span className="bp-placeholder">{t("raf_select_body_parts")}</span>}
        {value.map(p => (
          <span key={p} className="bp-chip">
            {p}
            <button type="button" onClick={e => { e.stopPropagation(); toggle(p); }}>×</button>
          </span>
        ))}
        <svg className="bp-arrow" viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
      </div>
      {open && (
        <div className="bp-dropdown">
          <input
            autoFocus
            className="bp-search"
            placeholder={t("raf_search_body_part")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setOpen(false)}
          />
          <div className="bp-options">
            {groups.map(({ group, items }) => (
              <div key={group} className="bp-group">
                <div className="bp-group-label">{group}</div>
                {items.map(p => (
                  <div key={p} className={`bp-option${value.includes(p) ? ' selected' : ''}`} onMouseDown={() => toggle(p)}>
                    <span className="bp-check">{value.includes(p) ? '✓' : ''}</span>
                    {p}
                  </div>
                ))}
              </div>
            ))}
            {customFiltered.length > 0 && (
              <div className="bp-group">
                <div className="bp-group-label">{t("raf_custom_parts")}</div>
                {customFiltered.map(p => (
                  <div key={p} className={`bp-option${value.includes(p) ? ' selected' : ''}`} onMouseDown={() => toggle(p)}>
                    <span className="bp-check">{value.includes(p) ? '✓' : ''}</span>
                    {p}
                  </div>
                ))}
              </div>
            )}
            {!adding ? (
              <div className="bp-add-trigger" onMouseDown={() => { setAdding(true); setSearch(''); }}>
                {t("raf_add_body_part")}
              </div>
            ) : (
              <div className="bp-add-row">
                <input
                  autoFocus
                  className="bp-add-input"
                  placeholder={t("raf_part_name_ph")}
                  value={newPart}
                  onChange={e => setNewPart(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addCustom(); if (e.key === 'Escape') setAdding(false); }}
                />
                <button type="button" className="bp-add-btn" onMouseDown={addCustom}>{t("raf_add")}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Select nativo (igual que InlineSelect en AccidentsTable) ────
function RaSelect({ options, value, onChange, placeholder }) {
  return (
    <select
      className="ra-inline-select"
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder || "Seleccionar..."}</option>
      {options.map(o => (
        <option key={o.key} value={o.value}>{o.text}</option>
      ))}
    </select>
  );
}

// ── Componente principal ──────────────────────────────────────
export function RiskAssessmentForm({ riskAssessment, close, onReload }) {
  const { accessToken, user } = useAuth();
  const { site }              = useSite();
  const { translations }      = useLanguage();
  const t = (key) => translations[key] || key;
  const canAdd = isAdmin(user?.role) || isMaster(user?.role);

  const [saving,      setSaving]      = useState(false);
  const [sectors,     setSectors]     = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const ra = riskAssessment || {};

  const [form, setForm] = useState({
    methodology:     ra.methodology     || "5x5",
    // Identificación
    sector:               ra.sector?._id    || ra.sector    || "",
    jobPosition:          ra.jobPosition          || "",
    hazardSource:         ra.hazardSource         || "",
    hazardSourceOther:    ra.hazardSourceOther    || "",
    riskType:             ra.riskType             || "",
    riskTypeOther:        ra.riskTypeOther        || "",
    potentialDamage:      ra.potentialDamage      || "",
    potentialDamageOther: ra.potentialDamageOther || "",
    affectedParts:        ra.affectedParts        || [],
    hazardDescription:    ra.hazardDescription    || "",
    workersExposed:       ra.workersExposed       || "",
    exposureTime:         ra.exposureTime         || "",
    sensibleWorkers:      ra.sensibleWorkers      || "no",
    // 5×5
    severity:          ra.severity          ?? "",
    exposureFrequency: ra.exposureFrequency ?? "",
    // NTP 330
    nd: ra.nd ?? "",
    ne: ra.ne ?? "",
    nc: ra.nc ?? "",
    // INSST
    probability:       ra.probability       || "",
    damageConsequence: ra.damageConsequence || "",
    // Controles
    controlHierarchy:       ra.controlHierarchy       || "",
    specificControlMeasure: ra.specificControlMeasure || "",
    controlStatus:          ra.controlStatus          || "NONE",
    controlResponsible:     ra.controlResponsible     || "",
    evaluationDate:         ra.evaluationDate         ? ra.evaluationDate.split("T")[0] : "",
    nextReassessment:       ra.nextReassessment       ? ra.nextReassessment.split("T")[0] : "",
    evaluator:              ra.evaluator              || "",
    approvedBy:             ra.approvedBy             || "",
    // Riesgo residual 5×5
    residualSeverity: ra.residualSeverity ?? "",
    residualFoe:      ra.residualFoe      ?? "",
    // Riesgo residual NTP 330
    residualNd: ra.residualNd ?? "",
    residualNe: ra.residualNe ?? "",
    residualNc: ra.residualNc ?? "",
    // Riesgo residual INSST
    residualProbability:       ra.residualProbability       || "",
    residualDamageConsequence: ra.residualDamageConsequence || "",
    // Legado
    residualRisk: ra.residualRisk || "",
    recordStatus: ra.recordStatus || "",
    // Plan de acción
    generateActionPlan: ra.generateActionPlan || false,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await sectorController.getSectors(accessToken, true);
        setSectors((res || []).map(s => ({ key: s._id, value: s._id, text: s.name })));
      } catch { setSectors([]); }
    })();
  }, [accessToken]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "methodology") {
      setFieldErrors({});
    } else if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // ── Cálculos en tiempo real ──────────────────────────────
  const m = form.methodology;

  const level5x5     = calc5x5Level(form.severity, form.exposureFrequency);
  const ntpVals      = calcNTPValues(form.nd, form.ne, form.nc);
  const levelNTP     = calcNTPLevel(form.nd, form.ne, form.nc);
  const levelINSST   = calcINSSTLevel(form.probability, form.damageConsequence);

  const resLevel5x5  = calc5x5Level(form.residualSeverity, form.residualFoe);
  const resNTPVals   = calcNTPValues(form.residualNd, form.residualNe, form.residualNc);
  const resLevelNTP  = calcNTPLevel(form.residualNd, form.residualNe, form.residualNc);
  const resLevelINSST = calcINSSTLevel(form.residualProbability, form.residualDamageConsequence);

  // ── Guardar ──────────────────────────────────────────────
  const handleSubmit = async () => {
    // Validación mínima
    if (!site) {
      toast.error(t("raf_select_site_first"));
      return;
    }
    const errors = {};
    if (!form.jobPosition)  errors.jobPosition  = true;
    if (!form.hazardSource) errors.hazardSource = true;
    if (!form.riskType)     errors.riskType = true;
    if (m === "5x5") {
      if (!form.severity)          errors.severity = true;
      if (!form.exposureFrequency) errors.exposureFrequency = true;
    }
    if (m === "NTP330") {
      if (form.nd === "") errors.nd = true;
      if (!form.ne)       errors.ne = true;
      if (!form.nc)       errors.nc = true;
    }
    if (m === "INSST") {
      if (!form.probability)       errors.probability = true;
      if (!form.damageConsequence) errors.damageConsequence = true;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error(t("raf_fill_required"));
      return;
    }
    setFieldErrors({});

    // Calcular riskResult + riskLevel
    let riskResult = null;
    let riskLevel  = null;
    if (m === "5x5" && level5x5) {
      riskResult = LEVEL_TO_RESULT[level5x5];
      riskLevel  = Number(form.severity) * Number(form.exposureFrequency);
    } else if (m === "NTP330" && ntpVals) {
      riskResult = LEVEL_TO_RESULT[levelNTP];
      riskLevel  = ntpVals.nr;
    } else if (m === "INSST" && levelINSST) {
      riskResult = LEVEL_TO_RESULT[levelINSST];
    }

    // Calcular residualRisk
    let residualRisk = form.residualRisk || null;
    if (m === "5x5" && resLevel5x5) {
      residualRisk = LEVEL_TO_RESIDUAL[resLevel5x5];
    } else if (m === "NTP330" && resLevelNTP) {
      residualRisk = LEVEL_TO_RESIDUAL[resLevelNTP];
    } else if (m === "INSST" && resLevelINSST) {
      residualRisk = LEVEL_TO_RESIDUAL[resLevelINSST];
    }

    setSaving(true);
    try {
      const toNum = (v) => (v !== "" && v !== undefined && v !== null ? Number(v) : undefined);
      const payload = {
        ...form,
        site,
        riskResult,
        riskLevel,
        residualRisk,
        severity:          toNum(form.severity),
        exposureFrequency: toNum(form.exposureFrequency),
        nd: toNum(form.nd),
        ne: toNum(form.ne),
        nc: toNum(form.nc),
        residualSeverity: toNum(form.residualSeverity),
        residualFoe:      toNum(form.residualFoe),
        residualNd: toNum(form.residualNd),
        residualNe: toNum(form.residualNe),
        residualNc: toNum(form.residualNc),
      };
      if (riskAssessment) {
        await riskAssessmentController.updateRiskAssessment(accessToken, riskAssessment._id, payload);
        toast.success(t("raf_updated"));
      } else {
        await riskAssessmentController.createRiskAssessment(accessToken, payload);
        toast.success(t("raf_saved"));
      }
      onReload();
      close();
    } catch (err) {
      toast.error(err?.msg || t("raf_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ra-form">
      <div className="ra-form__scroll">
      <Form>

        {/* ── Selector de metodología ── */}
        <SectionCard title={t("raf_sec_methodology")}>
          <div className="ra-meth-selector">
            {[
              { value: "5x5",    icon: "5×5",         title: "Matriz 5×5",    sub: t("raf_meth_5x5_sub"),   cls: "m5x5"  },
              { value: "NTP330", icon: "NTP\n330",     title: "NTP 330",       sub: t("raf_meth_ntp_sub"),   cls: "ntp"   },
              { value: "INSST",  icon: "INSST\n1996",  title: "Método INSST",  sub: t("raf_meth_insst_sub"), cls: "insst" },
            ].map(opt => (
              <label
                key={opt.value}
                className={`ra-meth-opt${form.methodology === opt.value ? " active" : ""}`}
                onClick={() => set("methodology", opt.value)}
              >
                <div className={`ra-meth-icon ${opt.cls}`}>{opt.icon}</div>
                <div className="ra-meth-body">
                  <div className="ra-meth-title">{opt.title}</div>
                  <div className="ra-meth-sub">{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>
        </SectionCard>

        {/* ── 1 · Identificación ── */}
        <SectionCard title={t("raf_sec_identification")} collapsible>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_sector_area_label")}</label>
              <RaSelect
                options={sectors}
                value={form.sector}
                onChange={v => set("sector", v)}
                placeholder={t("raf_select_sector")}
              />
            </Form.Field>
            <Form.Field required error={!!fieldErrors.jobPosition}>
              <label>{t("raf_occupation")}</label>
              <OcupacionDropdown
                accessToken={accessToken}
                value={form.jobPosition}
                onChange={v => set("jobPosition", v)}
                canAdd={canAdd}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={!!fieldErrors.hazardSource}>
              <label>{t("raf_hazard_source_label")}</label>
              <SourceDropdown
                accessToken={accessToken}
                value={form.hazardSource}
                onChange={v => set("hazardSource", v)}
                canAdd={canAdd}
              />
            </Form.Field>
            <Form.Field required error={!!fieldErrors.riskType}>
              <label>{t("raf_risk_type_label")}</label>
              <RiskTypeDropdown
                accessToken={accessToken}
                value={form.riskType}
                onChange={v => set("riskType", v)}
                canAdd={canAdd}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_potential_damage_label")}</label>
              <PotentialDamageDropdown
                accessToken={accessToken}
                value={form.potentialDamage}
                onChange={v => set("potentialDamage", v)}
                canAdd={canAdd}
              />
            </Form.Field>
            <Form.Field>
              <label>{t("raf_affected_parts")}</label>
              <BodyPartsSelect
                value={form.affectedParts}
                onChange={v => set("affectedParts", v)}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_sensitive_workers")}</label>
              <RaSelect
                options={[{ key: "no", value: "no", text: t("raf_no") }, { key: "yes", value: "yes", text: t("raf_yes") }]}
                value={form.sensibleWorkers}
                onChange={v => set("sensibleWorkers", v)}
                placeholder={t("raf_select")}
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>{t("raf_hazard_desc_label")}</label>
            <Form.TextArea
              rows={2}
              placeholder={t("raf_hazard_desc_ph")}
              value={form.hazardDescription}
              onChange={e => set("hazardDescription", e.target.value)}
            />
          </Form.Field>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_workers_exposed")}</label>
              <Form.Input type="number" min="0" placeholder="0" value={form.workersExposed} onChange={e => set("workersExposed", e.target.value)} />
            </Form.Field>
            <Form.Field>
              <label>{t("raf_exposure_time")}</label>
              <Form.Input placeholder={t("raf_exposure_time_ph")} value={form.exposureTime} onChange={e => set("exposureTime", e.target.value)} />
            </Form.Field>
          </Form.Group>
        </SectionCard>

        {/* ── 2A · Evaluación 5×5 ── */}
        {m === "5x5" && (
          <SectionCard title={t("raf_sec_eval_5x5")} accent="m5x5" collapsible>
            <div className="ra-meth-info m5x5">
              {t("raf_5x5_info")}
            </div>
            <Form.Group widths="equal">
              <Form.Field required error={!!fieldErrors.severity}>
                <label>{t("raf_severity_consequence")}</label>
                <RaSelect options={SEVERITY_5x5.map(o => ({...o, text: t(o.tKey)}))} value={form.severity} onChange={v => set("severity", v === "" ? "" : Number(v))} placeholder={t("raf_select")} />
              </Form.Field>
              <Form.Field required error={!!fieldErrors.exposureFrequency}>
                <label>{t("raf_foe_label")}</label>
                <RaSelect options={FOE_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.exposureFrequency} onChange={v => set("exposureFrequency", v === "" ? "" : Number(v))} placeholder={t("raf_select")} />
              </Form.Field>
            </Form.Group>
            <CalcResult
              formula={`Sev ${form.severity || "—"} × FoE ${form.exposureFrequency || "—"} = ${t("raf_risk_value")}`}
              value={form.severity && form.exposureFrequency ? Number(form.severity) * Number(form.exposureFrequency) : null}
              level={level5x5}
            />
          </SectionCard>
        )}

        {/* ── 2B · NTP 330 ── */}
        {m === "NTP330" && (
          <SectionCard title={t("raf_sec_eval_ntp")} accent="ntp" collapsible>
            <div className="ra-meth-info ntp">
              {t("raf_ntp_info")}
              <span className="ra-meth-info-src">{t("raf_ntp_source")}</span>
            </div>
            <Form.Group widths="equal">
              <Form.Field required error={!!fieldErrors.nd}>
                <label>{t("raf_nd_label")}</label>
                <RaSelect options={ND_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.nd} onChange={v => set("nd", v === "" ? "" : Number(v))} placeholder={t("raf_select")} />
              </Form.Field>
              <Form.Field required error={!!fieldErrors.ne}>
                <label>{t("raf_ne_label")}</label>
                <RaSelect options={NE_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.ne} onChange={v => set("ne", v === "" ? "" : Number(v))} placeholder={t("raf_select")} />
              </Form.Field>
            </Form.Group>
            <Form.Field required error={!!fieldErrors.nc}>
              <label>{t("raf_nc_label")}</label>
              <RaSelect options={NC_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.nc} onChange={v => set("nc", v === "" ? "" : Number(v))} placeholder={t("raf_select")} />
            </Form.Field>
            <CalcResult
              formula={
                ntpVals
                  ? `ND ${form.nd} × NE ${form.ne} = NP ${ntpVals.np}   |   NP ${ntpVals.np} × NC ${form.nc} = NR`
                  : "ND — × NE — = NP —  |  NP — × NC — = NR"
              }
              value={ntpVals?.nr ?? null}
              level={levelNTP}
            />
          </SectionCard>
        )}

        {/* ── 2C · Método INSST ── */}
        {m === "INSST" && (
          <SectionCard title={t("raf_sec_eval_insst")} accent="insst" collapsible>
            <div className="ra-meth-info insst">
              {t("raf_insst_info")}
              <span className="ra-meth-info-src">{t("raf_insst_source")}</span>
            </div>
            <Form.Group widths="equal">
              <Form.Field required error={!!fieldErrors.probability}>
                <label>{t("raf_damage_probability")}</label>
                <RaSelect options={PROB_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.probability} onChange={v => set("probability", v)} placeholder={t("raf_select")} />
              </Form.Field>
              <Form.Field required error={!!fieldErrors.damageConsequence}>
                <label>{t("raf_damage_severity_cons")}</label>
                <RaSelect options={CONS_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.damageConsequence} onChange={v => set("damageConsequence", v)} placeholder={t("raf_select")} />
              </Form.Field>
            </Form.Group>
            <CalcResult
              formula={`${t("raf_damage_probability")} ${form.probability || "—"} × ${t("raf_damage_severity_cons")} ${form.damageConsequence || "—"} = ${t("raf_risk_level_calc")}`}
              value={form.probability && form.damageConsequence ? `${form.probability}×${form.damageConsequence}` : null}
              level={levelINSST}
            />
          </SectionCard>
        )}

        {/* ── 3 · Controles ── */}
        <SectionCard title={t("raf_sec_controls")} collapsible>
          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_control_hierarchy_label")}</label>
              <RaSelect options={CONTROL_HIERARCHY_OPTIONS.map(o => ({...o, text: t(o.value)}))} value={form.controlHierarchy} onChange={v => set("controlHierarchy", v)} placeholder={t("raf_select_hierarchy")} />
            </Form.Field>
            <Form.Field>
              <label>{t("ra_control_status")}</label>
              <RaSelect options={CONTROL_STATUS_OPTIONS.map(o => ({...o, text: t(o.tKey)}))} value={form.controlStatus} onChange={v => set("controlStatus", v)} placeholder={t("raf_select_status")} />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>{t("raf_specific_control")}</label>
            <Form.TextArea rows={2} placeholder={t("raf_specific_control_ph")}
              value={form.specificControlMeasure} onChange={e => set("specificControlMeasure", e.target.value)} />
          </Form.Field>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_control_responsible")}</label>
              <Form.Input placeholder={t("raf_control_responsible_ph")} value={form.controlResponsible} onChange={e => set("controlResponsible", e.target.value)} />
            </Form.Field>
            <Form.Field>
              <label>{t("raf_evaluator_label")}</label>
              <Form.Input placeholder={t("raf_evaluator_ph")} value={form.evaluator} onChange={e => set("evaluator", e.target.value)} />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>{t("raf_evaluation_date")}</label>
              <Form.Input type="date" value={form.evaluationDate} onChange={e => set("evaluationDate", e.target.value)} />
            </Form.Field>
            <Form.Field>
              <label>{t("raf_next_reassessment_label")}</label>
              <Form.Input type="date" value={form.nextReassessment} onChange={e => set("nextReassessment", e.target.value)} />
            </Form.Field>
            <Form.Field>
              <label>{t("raf_approved_by")}</label>
              <Form.Input placeholder={t("raf_approved_by_ph")} value={form.approvedBy} onChange={e => set("approvedBy", e.target.value)} />
            </Form.Field>
          </Form.Group>
        </SectionCard>

        {/* ── 4A · Residual 5×5 ── */}
        {m === "5x5" && (
          <SectionCard title={t("raf_sec_residual_5x5")} collapsible>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("raf_residual_severity")}</label>
                <RaSelect options={SEVERITY_5x5_SHORT.map(o => ({...o, text: t(o.tKey)}))} value={form.residualSeverity} onChange={v => set("residualSeverity", v === "" ? "" : Number(v))} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
              <Form.Field>
                <label>{t("raf_residual_foe")}</label>
                <RaSelect options={FOE_SHORT.map(o => ({...o, text: t(o.tKey)}))} value={form.residualFoe} onChange={v => set("residualFoe", v === "" ? "" : Number(v))} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
            </Form.Group>
            {(form.residualSeverity || form.residualFoe) && (
              <CalcResult
                formula={`Sev R ${form.residualSeverity || "—"} × FoE R ${form.residualFoe || "—"} = ${t("raf_residual")}`}
                value={form.residualSeverity && form.residualFoe ? Number(form.residualSeverity) * Number(form.residualFoe) : null}
                level={resLevel5x5}
              />
            )}
          </SectionCard>
        )}

        {/* ── 4B · Residual NTP 330 ── */}
        {m === "NTP330" && (
          <SectionCard title={t("raf_sec_residual_ntp")} collapsible>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("raf_residual_nd")}</label>
                <RaSelect options={ND_OPTIONS.map(o => ({ ...o, text: t(o.tKey).split(":")[0] }))} value={form.residualNd} onChange={v => set("residualNd", v === "" ? "" : Number(v))} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
              <Form.Field>
                <label>{t("raf_residual_ne")}</label>
                <RaSelect options={NE_OPTIONS.map(o => ({ ...o, text: t(o.tKey).split(":")[0] }))} value={form.residualNe} onChange={v => set("residualNe", v === "" ? "" : Number(v))} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
              <Form.Field>
                <label>{t("raf_residual_nc")}</label>
                <RaSelect options={NC_OPTIONS.map(o => ({ ...o, text: t(o.tKey).split(":")[0] }))} value={form.residualNc} onChange={v => set("residualNc", v === "" ? "" : Number(v))} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
            </Form.Group>
            {(form.residualNd !== "" || form.residualNe || form.residualNc) && (
              <CalcResult
                formula={t("raf_nr_residual_formula")}
                value={resNTPVals?.nr ?? null}
                level={resLevelNTP}
              />
            )}
          </SectionCard>
        )}

        {/* ── 4C · Residual INSST ── */}
        {m === "INSST" && (
          <SectionCard title={t("raf_sec_residual_insst")} collapsible>
            <Form.Group widths="equal">
              <Form.Field>
                <label>{t("raf_residual_probability")}</label>
                <RaSelect options={PROB_OPTIONS.map(o => ({ ...o, text: t(o.tKey) }))} value={form.residualProbability} onChange={v => set("residualProbability", v)} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
              <Form.Field>
                <label>{t("raf_residual_severity")}</label>
                <RaSelect options={CONS_OPTIONS.map(o => ({ ...o, text: t(o.tKey) }))} value={form.residualDamageConsequence} onChange={v => set("residualDamageConsequence", v)} placeholder={t("raf_no_reevaluation")} />
              </Form.Field>
            </Form.Group>
            {(form.residualProbability || form.residualDamageConsequence) && (
              <CalcResult
                formula={`${t("raf_residual_probability")} R ${form.residualProbability || "—"} × ${t("raf_residual_severity")} R ${form.residualDamageConsequence || "—"} = ${t("raf_residual")}`}
                value={form.residualProbability && form.residualDamageConsequence ? `${form.residualProbability}×${form.residualDamageConsequence}` : null}
                level={resLevelINSST}
              />
            )}
          </SectionCard>
        )}

        {/* ── Toggle Plan de Acción ── */}
        <div className="ra-action-plan-toggle">
          {ra.actionPlanId && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: 10 }}>
              <span style={{ width: 16, height: 16, borderRadius: 3, background: "#2E9461", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 12 12" fill="white" width="9" height="9"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: "13px", color: "#2E9461" }}>{t("raf_action_linked")}</div>
                <div style={{ fontSize: "11px", color: "#6B7280", marginTop: 2 }}>
                  {t("raf_action_linked_desc")}
                </div>
              </div>
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.generateActionPlan}
              onChange={e => set("generateActionPlan", e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: "13px" }}>
                {ra.actionPlanId ? t("raf_generate_action_add") : t("raf_generate_action")}
              </div>
              <div style={{ fontSize: "11px", color: "#6B7280", marginTop: 2 }}>
                {ra.actionPlanId ? t("raf_generate_action_add_desc") : t("raf_generate_action_desc")}
              </div>
            </div>
          </label>
        </div>

      </Form>
      </div>
      <div className="ra-form__footer">
        <Button primary loading={saving} disabled={saving} onClick={handleSubmit}>
          <Icon name="save" /> {riskAssessment ? t("raf_update") : t("save")}
        </Button>
        <button className="ra-form__cancel-btn" type="button" onClick={close} disabled={saving}>
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}
